import { useEffect, useMemo, useState } from 'react'
import { ExternalLink, Package, User } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Button from '../common/Button.js'
import Loader from '../common/Loader.js'
import { settlementModalOverlayClass, settlementModalPanelWideClass } from '../common/uiClasses.js'
import { shipmentService } from '../../services/shipmentService.js'
import { formatIndianDate, formatIndianTime } from '../../utils/localDate.js'
import {
  dedupeDisplayFields,
  describeParcelRow,
  fieldLabel,
  formatReviewFieldValue,
  groupsWithChanges,
  parcelDiffSummary,
  parcelHasDimensions,
  resolveInstrumentFromShipment,
} from '../../utils/shipmentEditRequestReview.js'

const statusBadgeClass = {
  added: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  removed: 'bg-red-50 text-red-800 border-red-200',
  changed: 'bg-amber-50 text-amber-900 border-amber-200',
  unchanged: 'bg-gray-50 text-gray-600 border-gray-200',
}

const statusLabel = {
  added: 'New',
  removed: 'Removed',
  changed: 'Updated',
  unchanged: 'Same',
}

const MetaItem = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">{label}</p>
    <p className="text-sm text-gray-900 break-words mt-0.5">{value || '—'}</p>
  </div>
)

const FieldDiffRow = ({ label, current, proposed }) => {
  const changed = current !== proposed
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-[minmax(0,26%)_1fr_1fr] gap-2 sm:gap-0 py-2.5 border-b border-blue-100/80 last:border-0 ${changed ? 'bg-sky-50/50' : ''}`}>
      <div className="text-xs sm:text-sm font-medium text-blue-950 sm:px-3 sm:pt-0.5">{label}</div>
      <div className="sm:px-3 sm:border-l sm:border-blue-100 min-w-0">
        <p className="text-[10px] uppercase text-gray-500 sm:hidden mb-0.5">Current</p>
        <p className="text-sm text-gray-700 break-words whitespace-pre-wrap">{current}</p>
      </div>
      <div className="sm:px-3 sm:border-l sm:border-blue-100 min-w-0 bg-blue-50 rounded-lg sm:rounded-none px-2 py-1.5 sm:py-0">
        <p className="text-[10px] uppercase text-blue-700/80 sm:hidden mb-0.5">Requested</p>
        <p className={`text-sm break-words whitespace-pre-wrap ${changed ? 'text-blue-950 font-semibold' : 'text-gray-800'}`}>{proposed}</p>
      </div>
    </div>
  )
}

const ParcelsReviewSection = ({ current, proposed, cargoHint }) => {
  const summary = useMemo(() => parcelDiffSummary(current, proposed), [current, proposed])
  const { rows } = summary

  return (
    <section className="border border-blue-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="bg-blue-100 text-blue-950 px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-b border-blue-200">
        <div className="flex items-center gap-2">
          <Package size={18} className="shrink-0 text-blue-800" />
          <h3 className="font-semibold text-sm sm:text-base">Articles / parcels</h3>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="bg-white/80 text-blue-900 px-2 py-0.5 rounded-full border border-blue-200">{summary.totalBefore} → {summary.totalAfter} lines</span>
          {summary.added ? <span className="bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded-full border border-emerald-200">{summary.added} new</span> : null}
          {summary.changed ? <span className="bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full border border-amber-200">{summary.changed} updated</span> : null}
          {summary.removed ? <span className="bg-red-100 text-red-900 px-2 py-0.5 rounded-full border border-red-200">{summary.removed} removed</span> : null}
        </div>
      </div>

      <div className="hidden sm:grid grid-cols-[72px_1fr_1fr] gap-0 border-b border-blue-100 text-xs font-semibold uppercase tracking-wide">
        <div className="px-3 py-2 bg-slate-50 text-gray-600">#</div>
        <div className="px-3 py-2 border-l border-blue-100 bg-slate-50 text-gray-600">Current booking</div>
        <div className="px-3 py-2 border-l border-blue-200 bg-blue-50 text-blue-900">Agent request</div>
      </div>

      <div className="divide-y divide-blue-100 max-h-[min(38vh,380px)] overflow-y-auto overscroll-contain">
        {rows.map((row) => (
          <div
            key={`parcel-row-${row.index}`}
            className="grid grid-cols-1 sm:grid-cols-[72px_1fr_1fr] gap-0 sm:gap-0"
          >
            <div className="flex sm:block items-center gap-2 px-3 py-2 bg-gray-50/80 sm:bg-transparent border-b sm:border-b-0 border-gray-100">
              <span className="text-xs font-semibold text-gray-500 sm:hidden">Line</span>
              <span className="text-sm font-semibold text-gray-800">{row.index}</span>
              <span className={`inline-flex sm:mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${statusBadgeClass[row.status]}`}>
                {statusLabel[row.status]}
              </span>
            </div>
            <div className="px-3 py-3 sm:border-l border-gray-100 min-w-0">
              <p className="text-[10px] uppercase text-gray-400 sm:hidden mb-1">Current</p>
              {row.current ? (
                <>
                  <p className="text-sm font-medium text-gray-800 break-words">{describeParcelRow(row.current, { cargoHint })}</p>
                  <ParcelDetailGrid row={row.current} cargoHint={cargoHint} />
                </>
              ) : (
                <p className="text-sm text-gray-400 italic">Not on this line</p>
              )}
            </div>
            <div className="px-3 py-3 sm:border-l border-blue-100 min-w-0 bg-blue-50">
              <p className="text-[10px] uppercase text-blue-700/80 sm:hidden mb-1">Requested</p>
              {row.proposed ? (
                <>
                  <p className="text-sm font-semibold text-gray-900 break-words">{describeParcelRow(row.proposed, { cargoHint })}</p>
                  <ParcelDetailGrid row={row.proposed} cargoHint={cargoHint} emphasized />
                </>
              ) : (
                <p className="text-sm text-gray-400 italic">Removed in request</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

const ParcelDetailGrid = ({ row, cargoHint, emphasized = false }) => {
  const showDims = cargoHint || parcelHasDimensions(row)
  const labelClass = emphasized ? 'text-gray-600' : 'text-gray-500'
  const valueClass = emphasized ? 'text-gray-900 font-medium' : 'text-gray-700'

  const cells = [
    { label: 'Type', value: row.article_type || row.package_name || '—' },
    { label: 'Qty', value: row.qty ?? 1 },
  ]
  if (showDims) {
    cells.push(
      { label: 'L × W × H (cm)', value: [row.length_cm, row.width_cm, row.height_cm].map((v) => v ?? '—').join(' × ') },
      { label: 'Actual wt (kg)', value: row.actual_weight ?? '—' },
      { label: 'Chargeable (kg)', value: row.chargeable_weight ?? '—' },
      { label: 'Volumetric (kg)', value: row.volumetric_weight ?? '—' },
      { label: 'CBM', value: row.cbm ?? '—' },
    )
  } else {
    cells.push(
      { label: 'Rate / article', value: row.article_amount != null ? `₹${Number(row.article_amount).toLocaleString('en-IN')}` : '—' },
      { label: 'Weight (kg)', value: row.chargeable_weight || row.actual_weight || '—' },
    )
  }

  return (
    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
      {cells.map((cell) => (
        <div key={cell.label} className="min-w-0">
          <dt className={`${labelClass} truncate`}>{cell.label}</dt>
          <dd className={`${valueClass} break-words`}>{cell.value}</dd>
        </div>
      ))}
    </dl>
  )
}

const ShipmentEditRequestReviewModal = ({ review, saving, remark, onRemarkChange, onCancel, onApprove, onReject }) => {
  const navigate = useNavigate()
  const [loadingContext, setLoadingContext] = useState(true)
  const [shipmentContext, setShipmentContext] = useState(null)

  useEffect(() => {
    if (!review?.shipment_id) return undefined
    let active = true
    setLoadingContext(true)
    shipmentService
      .getShipment(review.shipment_id)
      .then((response) => {
        if (active) setShipmentContext(response.data?.data || response.data?.shipment || response.data)
      })
      .catch(() => {
        if (active) setShipmentContext(null)
      })
      .finally(() => {
        if (active) setLoadingContext(false)
      })
    return () => { active = false }
  }, [review?.shipment_id])

  const displayFields = useMemo(
    () => dedupeDisplayFields(review?.changed_fields || []),
    [review?.changed_fields],
  )

  const fieldGroups = useMemo(() => groupsWithChanges(displayFields), [displayFields])
  const parcelsChanged = displayFields.includes('parcels')
  const nonParcelFields = displayFields.filter((key) => key !== 'parcels')

  const reviewContext = useMemo(() => {
    const hubNamesById = {}
    const creditClientNamesById = {}
    const ship = shipmentContext
    if (ship?.receiver_location_hub_id != null) {
      const hub = ship.receiver_location_hub
      const name = hub?.name || hub?.hub_name || ship.receiver_location_hub_name
      if (name) hubNamesById[String(ship.receiver_location_hub_id)] = name
    }
    if (ship?.shipment_client_id != null && ship?.shipment_client) {
      const client = ship.shipment_client
      const name = client.client_name || client.name
      if (name) creditClientNamesById[String(ship.shipment_client_id)] = name
    }
    return { hubNamesById, creditClientNamesById, ship }
  }, [shipmentContext])

  const cargoHint = String(shipmentContext?.shipment_type || '').toLowerCase() === 'cargo'

  const formatValue = (key, snapshot, side) => {
    let value = snapshot?.[key]
    if (
      key === 'payment_mode'
      && (value == null || value === '')
      && side === 'current'
      && reviewContext.ship
    ) {
      value = resolveInstrumentFromShipment(reviewContext.ship)
    }
    return formatReviewFieldValue(key, value, reviewContext)
  }

  if (!review) return null

  return (
    <div
      className={`${settlementModalOverlayClass} z-[90]`}
      role="presentation"
      onClick={saving ? undefined : onCancel}
    >
      <div
        className={`relative ${settlementModalPanelWideClass} min-h-0`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shipment-edit-review-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-4 sm:px-6 py-4 border-b border-blue-200 bg-gradient-to-r from-blue-50 via-sky-50 to-white shrink-0">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 id="shipment-edit-review-title" className="text-lg sm:text-xl font-bold text-gray-900">
                Review shipment update request
              </h2>
              <p className="text-sm text-gray-600 mt-1 break-all">
                <span className="font-medium text-gray-800">{review.shipment_id}</span>
                <span className="text-gray-400 mx-1">·</span>
                {review.request_id}
              </p>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase bg-amber-100 text-amber-900 border border-amber-200">
              Pending approval
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 rounded-xl bg-blue-50/80 border border-blue-100 shadow-sm">
            <MetaItem label="Agent" value={review.requested_by_name || review.requested_by} />
            <MetaItem
              label="Submitted"
              value={
                review.created_at
                  ? `${formatIndianDate(review.created_at)} · ${formatIndianTime(review.created_at)}`
                  : '—'
              }
            />
            <MetaItem label="Status when sent" value={review.shipment_status_at_request} />
            <MetaItem
              label="Route (current)"
              value={
                shipmentContext
                  ? `${shipmentContext.sender_location || '—'} → ${shipmentContext.receiver_location || '—'}`
                  : loadingContext ? 'Loading…' : '—'
              }
            />
          </div>

          <p className="text-xs text-gray-500 mt-3 leading-relaxed">
            Compare every field below before approving. Freight and totals may change when articles change;
            for <strong>Paid</strong> bookings, cashbook entries are updated on approve. Charge line edits are not part of this request.
          </p>

          {review.agent_remark ? (
            <div className="mt-3 flex gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100 text-sm text-blue-950">
              <User size={18} className="shrink-0 mt-0.5 opacity-70" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-800/80">Agent note</p>
                <p className="mt-0.5 break-words whitespace-pre-wrap">{review.agent_remark}</p>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 space-y-4 bg-slate-50/40">
          {loadingContext && !shipmentContext ? (
            <div className="py-8 flex justify-center">
              <Loader fullScreen={false} size="small" message="Loading shipment context…" />
            </div>
          ) : null}

          {parcelsChanged ? (
            <ParcelsReviewSection
              current={review.current_snapshot?.parcels}
              proposed={review.proposed_snapshot?.parcels}
              cargoHint={cargoHint}
            />
          ) : null}

          {fieldGroups.map((group) => (
            <section key={group.id} className="border border-blue-200 rounded-xl overflow-hidden bg-white shadow-sm">
              <div className="bg-blue-100 px-4 py-2.5 border-b border-blue-200">
                <h3 className="text-sm font-semibold text-blue-950">{group.title}</h3>
              </div>
              <div className="px-1 sm:px-2 py-1">
                <div className="hidden sm:grid grid-cols-[minmax(0,26%)_1fr_1fr] gap-0 py-2 text-[11px] font-semibold uppercase tracking-wide border-b border-blue-100">
                  <div className="px-3 text-blue-900">Field</div>
                  <div className="px-3 border-l border-blue-100 text-gray-600">Current</div>
                  <div className="px-3 border-l border-blue-100 text-blue-800 bg-blue-50/80">Requested</div>
                </div>
                {group.fields.map((key) => (
                  <FieldDiffRow
                    key={key}
                    label={fieldLabel(key)}
                    current={formatValue(key, review.current_snapshot, 'current')}
                    proposed={formatValue(key, review.proposed_snapshot, 'proposed')}
                  />
                ))}
              </div>
            </section>
          ))}

          {nonParcelFields.length === 0 && !parcelsChanged ? (
            <p className="text-sm text-gray-500 text-center py-6">No field changes recorded.</p>
          ) : null}

          <div className="flex justify-end">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-900 font-medium"
              onClick={() => navigate(`/shipments/view/${encodeURIComponent(review.shipment_id)}`)}
            >
              Open full shipment
              <ExternalLink size={14} />
            </button>
          </div>
        </div>

        <div className="shrink-0 px-4 sm:px-6 py-4 border-t border-blue-200 bg-blue-50/90">
          <label className="block text-sm font-medium text-gray-700 mb-1">Remark for agent</label>
          <textarea
            value={remark}
            onChange={(event) => onRemarkChange(event.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white"
            placeholder="Required when rejecting; optional when approving"
          />
          <div className="mt-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
            <Button variant="secondary" onClick={onReject} disabled={saving}>Reject</Button>
            <Button variant="primary" onClick={onApprove} loading={saving} disabled={saving}>Approve & apply</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShipmentEditRequestReviewModal
